import { motion } from "framer-motion";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header band — sits above the image/text grid */}
        <div className="grid lg:grid-cols-12 gap-10 mb-10 lg:items-end">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90"
            >
              About / Zevnox Search
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
              data-testid="about-heading"
            >
              Built for Financial Leaders.
              <br />
              <span className="zx-hi">Designed for Scale.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="about-supporting"
            className="lg:col-span-6 max-w-md lg:max-w-lg lg:pb-1 text-neutral-200 text-sm sm:text-base leading-relaxed lg:text-right lg:ml-auto"
          >
            Built for firms that operate globally — where precision hiring
            creates long‑term competitive advantage.
          </motion.p>
        </div>

        {/* Balanced content row — image height equals text height */}
        <div className="grid lg:grid-cols-12 gap-10 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 flex"
          >
            <div className="relative w-full min-h-[440px] lg:min-h-0 rounded-2xl overflow-hidden border border-white/8 zx-glow-ring">
              <img
                src="/about-hero.jpg"
                alt="Zevnox Search — global executive network connecting financial hubs"
                loading="lazy"
                decoding="async"
                width="640"
                height="640"
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  filter:
                    "contrast(1.20) saturate(1.22) brightness(1.06) hue-rotate(-2deg)",
                }}
              />
              {/* Directional top-left key light — premium subject lift */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-screen pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 70% 55% at 22% 18%, rgba(147,197,253,0.22), rgba(96,165,250,0) 65%)",
                }}
              />
              {/* Subject focus light on globe-in-hand */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-screen pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side at 38% 42%, rgba(96,165,250,0.24), rgba(96,165,250,0) 55%)",
                }}
              />
              {/* Warm soft accent on lower right */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-soft-light pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 75% 78%, rgba(217,249,157,0.18), transparent 60%)",
                }}
              />
              {/* Corner vignette — focuses gaze on subject */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(120% 90% at 50% 50%, transparent 35%, rgba(0,0,0,0.28) 78%, rgba(0,0,0,0.55) 100%)",
                }}
              />
              {/* Highlight sheen across top */}
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0) 100%)",
                }}
              />
              {/* Bottom integration with dark UI */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent, rgba(11,11,11,0.55) 60%, rgba(11,11,11,0.85))",
                }}
              />
              {/* Premium edge accent */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  boxShadow:
                    "inset 0 0 0 1px rgba(217,249,157,0.10), inset 0 0 60px rgba(96,165,250,0.10)",
                }}
              />
              <div className="absolute top-4 left-5 text-[10px] font-mono uppercase tracking-[0.26em] text-[#D9F99D]/95">
                ZX · Global Desks
              </div>
              <div className="absolute bottom-4 left-5 text-[11px] font-mono uppercase tracking-[0.22em] text-white/95">
                Zevnox Network · 28 global markets
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 flex flex-col justify-center space-y-6 text-neutral-200 text-base sm:text-lg leading-relaxed"
          >
            <p>
              <span className="text-white font-medium">Zevnox Search</span> is a
              high‑growth executive firm specialising in building
              high‑performance teams across Trading, FinTech, AI, and Digital
              Asset ecosystems worldwide.
            </p>
            <p>
              With a strong focus on client‑facing delivery roles, C‑level
              leadership, and engineering talent, we enable organisations to
              scale efficiently across global markets.
            </p>
            <p>
              At Zevnox Search, we combine deep domain expertise with a
              human‑first approach — ensuring long‑term alignment, seamless
              onboarding, and teams built to perform and grow.
            </p>
            <p className="text-white">
              We don&apos;t just fill roles — we build teams that drive
              meaningful business outcomes.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
